// src/components/settings/ZigbeeSettings.js
import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '../../context/AppContext';
import './SettingsPanel.css';
import { toast, ToastContainer } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
const ZigbeeSettings = ({ config }) => {
  const { updateConfigSection, restartZigbee2mqtt: contextRestartZigbee2mqtt } = useContext(AppContext);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [zigbee2mqttConfig, setZigbee2mqttConfig] = useState(null);
  const [zigbee2mqttLoading, setZigbee2mqttLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [zigbeeToken, setZigbeeToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const { apiRequest } = useAuth();
  
  // Initialize form state from config - simplified to only require URL
  const [formData, setFormData] = useState({
    websocketUrl: config.zigbee?.websocketUrl || 'ws://localhost:8080/api',
  });

  const API_BASE_URL = '';
  const API_PATH = '/api';

  const [zigbeeConnected, setZigbeeConnected] = useState(true);
  const [restartingZigbee, setRestartingZigbee] = useState(false);

  // Load Zigbee2MQTT config when component mounts or when showAdvanced changes
  useEffect(() => {
    if (showAdvanced) {
      fetchZigbee2mqttConfig();
    }
  }, [showAdvanced]);

  // Load Zigbee token when component mounts
  useEffect(() => {
    fetchZigbeeToken();
  }, []);

  const fetchZigbee2mqttConfig = async () => {
    setZigbee2mqttLoading(true);
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/config`);
      if (!response.ok) {
        // If 404 or other error, assume the config file doesn't exist or couldn't be accessed
        // but still allow user to create/modify config
        if (response.status === 404) {
          setZigbeeConnected(false);
          setZigbee2mqttConfig({
            // Default empty configuration that user can modify
            permit_join: false,
            serial: {
              port: formData.serialPort || 'tcp://slzb-06.local:6638'
            },
            mqtt: {
              base_topic: 'zigbee2mqtt',
              server: 'mqtt://localhost:1883'
            },
            advanced: {
              channel: parseInt(formData.channel, 10) || 15,
              pan_id: formData.panId || undefined
            }
          });
          return;
        }
        throw new Error(`Failed to fetch Zigbee2MQTT config: ${response.statusText}`);
      }
      // const data = await response.json();
      setZigbee2mqttConfig(data.config);
      setZigbeeConnected(true);
    } catch (error) {
      console.error('Error fetching Zigbee2MQTT config:', error);
      setZigbeeConnected(false);
      // Still allow editing by providing a default config
      setZigbee2mqttConfig({
        permit_join: false,
        serial: {
          port: formData.serialPort || 'tcp://slzb-06.local:6638',
        },
        mqtt: {
          base_topic: 'zigbee2mqtt',
          server: 'mqtt://localhost:1883'
        },
        advanced: {
          channel: parseInt(formData.channel, 10) || 15,
          pan_id: formData.panId || undefined
        }
      });
      setStatus({
        type: 'warning',
        message: `Zigbee2MQTT configuration file not found or inaccessible. You can create a new configuration.`
      });
    } finally {
      setZigbee2mqttLoading(false);
    }
  };

  const fetchZigbeeToken = async () => {
    setTokenLoading(true);
    try {
      const {response, data} = await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee/token`);
      if (response.ok) {
        setZigbeeToken(data.token);
      }
    } catch (error) {
      console.error('Error fetching Zigbee token:', error);
    } finally {
      setTokenLoading(false);
    }
  };
  
  const restartZigbee2mqtt = async () => {
    setRestartingZigbee(true);
    try {
      await contextRestartZigbee2mqtt();
      
      setStatus({
        type: 'success',
        message: 'Zigbee2MQTT service restart initiated. It may take a few moments to reconnect.'
      });
      
      // Set a timer to check if connection is restored after a short delay
      setTimeout(() => {
        fetchZigbee2mqttConfig();
      }, 5000);
      
    } catch (error) {
      console.error('Error restarting Zigbee2MQTT:', error);
      setStatus({
        type: 'error',
        message: `Failed to restart Zigbee2MQTT: ${error.message}`
      });
    } finally {
      setRestartingZigbee(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    
    try {
      await updateConfigSection('zigbee', formData);
      setStatus({
        type: 'success',
        message: 'Zigbee settings updated successfully. Reconnecting to Zigbee network...'
      });
      
      // Refresh Zigbee2MQTT config if we're showing advanced settings
      if (showAdvanced) {
        await fetchZigbee2mqttConfig();
      }
      
      // Refresh the token display
      await fetchZigbeeToken();
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to update Zigbee settings.'
      });
    } finally {
      setSaving(false);
    }
  };

  const updateZigbee2mqttConfigFile = async () => {
    if (!zigbee2mqttConfig) return;
    
    setSaving(true);
    setStatus(null);
    
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/zigbee2mqtt/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ config: zigbee2mqttConfig })
      });
      
      if (!response.ok) {
        const errorData = data;
        throw new Error(errorData.message || 'Failed to update Zigbee2MQTT configuration');
      }
      
      setStatus({
        type: 'success',
        message: 'Zigbee2MQTT configuration updated successfully.'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to update Zigbee2MQTT configuration.'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-section">
      <h2>Zigbee Configuration</h2>
      <div className="status-indicator-row">
        <p>Configure Zigbee network settings for connecting with smart home devices.</p>
        <div className={`connection-status ${zigbeeConnected ? 'connected' : 'disconnected'}`}>
          {zigbeeConnected ? 'Connected' : 'Disconnected'}
        </div>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="websocketUrl">Zigbee2MQTT WebSocket URL</label>
          <input
            type="text"
            id="websocketUrl"
            name="websocketUrl"
            value={formData.websocketUrl}
            onChange={handleChange}
            required
          />
          <div className="form-help">
            WebSocket URL for connecting to Zigbee2MQTT (e.g., ws://localhost:8080/api?token=your_token)
          </div>
        </div>

        <div className="form-group">
          <label>Stored Zigbee Token</label>
          <div className="token-display">
            {tokenLoading ? (
              <span className="loading">Loading token...</span>
            ) : zigbeeToken ? (
              <code className="token-value">{zigbeeToken}</code>
            ) : (
              <span className="no-token">No token found</span>
            )}
          </div>
          <div className="form-help">
            This token is automatically extracted from the WebSocket URL and stored securely.
          </div>
        </div>
        
        {status && (
          <div className={`form-status ${status.type}`}>
            {status.message}
          </div>
        )}
        
        <div className="form-buttons">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={restartZigbee2mqtt} 
            disabled={restartingZigbee || saving}
          >
            {restartingZigbee ? 'Restarting...' : 'Restart Zigbee2MQTT'}
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
      
      <div className="advanced-settings">
        <div className="section-divider"></div>
        <button
          className="btn btn-text"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced Zigbee2MQTT Configuration
        </button>
        
        {showAdvanced && (
          <div className="zigbee2mqtt-config">
            <h3>Zigbee2MQTT Configuration File</h3>
            {!zigbeeConnected && (
              <div className="alert-box warning">
                <strong>Note:</strong> Zigbee2MQTT appears to be disconnected or the configuration file is not accessible. 
                You can still edit and create a configuration file, but you will need to restart the service for changes to take effect.
              </div>
            )}
            <p>This is the direct content of /opt/zigbee2mqtt/data/configuration.yaml. Changes made here will require a restart of the Zigbee2MQTT service.</p>
            
            {zigbee2mqttLoading ? (
              <div className="loading">Loading Zigbee2MQTT configuration...</div>
            ) : zigbee2mqttConfig ? (
              <div>
                <div className="config-editor">
                  <pre 
                    className="config-display"
                    contentEditable={true}
                    suppressContentEditableWarning={true}
                    onBlur={(e) => {
                      try {
                        const newConfig = JSON.parse(e.target.innerText);
                        setZigbee2mqttConfig(newConfig);
                      } catch (error) {
                        console.error('Invalid JSON:', error);
                        toast.error('Invalid JSON configuration. Please check your syntax.');
                        // Revert to the original content
                        e.target.innerText = JSON.stringify(zigbee2mqttConfig, null, 2);
                      }
                    }}
                  >
                    {JSON.stringify(zigbee2mqttConfig, null, 2)}
                  </pre>
                </div>
                <div className="form-buttons">
                  <button
                    className="btn btn-secondary"
                    onClick={updateZigbee2mqttConfigFile}
                    disabled={saving}
                  >
                    {saving ? 'Updating...' : 'Update Zigbee2MQTT Config File'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={restartZigbee2mqtt}
                    disabled={restartingZigbee}
                  >
                    {restartingZigbee ? 'Restarting...' : 'Restart Zigbee2MQTT'}
                  </button>
                </div>
                <div className="form-help mt-2">
                  Note: Changes to the Zigbee2MQTT configuration file require restarting the Zigbee2MQTT service.
                </div>
              </div>
            ) : (
              <div className="error-message">
                Failed to load Zigbee2MQTT configuration. You can create a new configuration file by updating the settings above with the "Update Zigbee2MQTT Configuration" option checked.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ZigbeeSettings;